import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useTitle } from '../context/TitleContext';
import IconifyIcon from '../components/IconifyIcon';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { setTitle } = useTitle();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  useEffect(() => {
    setTitle('Mon Profil');
  }, [setTitle]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || ''
      });
      if (user.image) {
        setImagePreview(user.image);
      }
    }
  }, [user]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await updateProfile(profileData);
      if (result.success) {
        setSuccess('Profil mis à jour avec succès!');
      } else {
        setError(result.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await updateProfile({
        current_password: passwordData.current_password,
        password: passwordData.new_password,
        password_confirmation: passwordData.new_password_confirmation
      });
      
      if (result.success) {
        setSuccess('Mot de passe modifié avec succès!');
        setPasswordData({ current_password: '', new_password: '', new_password_confirmation: '' });
      } else {
        setError(result.error || 'Erreur lors de la modification');
      }
    } catch (err) {
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const getRoleInfo = (role) => {
    const roles = {
      admin: { label: 'Administrateur', color: '#dc3545', icon: 'bx:crown' },
      employee: { label: 'Employé', color: '#6f42c1', icon: 'bx:briefcase' },
      client: { label: 'Client', color: '#198754', icon: 'bx:user' }
    };
    return roles[role] || { label: role, color: '#6c757d', icon: 'bx:user' };
  };

  const roleInfo = getRoleInfo(user?.role);

  return (
    <div className="profile-page">
      <style>{`
        .profile-page {
          background: #f8fafc;
          min-height: 100vh;
          padding: 15px;
        }
        
        @media (min-width: 768px) {
          .profile-page {
            padding: 30px;
          }
        }
        
        .profile-header-card {
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 20px;
        }
        
        @media (min-width: 768px) {
          .profile-header-card {
            border-radius: 24px;
            margin-bottom: 24px;
          }
        }
        
        .profile-header-inner {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          padding: 25px 20px;
        }
        
        @media (min-width: 768px) {
          .profile-header-inner {
            padding: 50px 40px;
          }
        }
        
        .profile-avatar-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #c5a05e 0%, #dbb978 100%);
          padding: 4px;
          box-shadow: 0 15px 40px rgba(0,0,0,0.25);
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-avatar-wrapper {
            width: 130px;
            height: 130px;
            padding: 5px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          }
        }
        
        .profile-avatar-inner {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: #1e293b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 700;
          color: #c5a05e;
        }
        
        @media (min-width: 768px) {
          .profile-avatar-inner {
            font-size: 52px;
          }
        }
        
        .profile-header-info h1 {
          color: white;
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 6px;
        }
        
        @media (min-width: 768px) {
          .profile-header-info h1 {
            font-size: 32px;
            margin-bottom: 8px;
          }
        }
        
        .profile-header-info p {
          color: rgba(255,255,255,0.7);
          font-size: 13px;
          margin-bottom: 12px;
        }
        
        @media (min-width: 768px) {
          .profile-header-info p {
            font-size: 16px;
            margin-bottom: 16px;
          }
        }
        
        .profile-role-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 50px;
          color: white;
          font-size: 12px;
          font-weight: 600;
        }
        
        @media (min-width: 768px) {
          .profile-role-badge {
            padding: 8px 16px;
            font-size: 14px;
          }
        }
        
        .profile-member-since {
          color: rgba(255,255,255,0.5);
          font-size: 11px;
          margin-top: 8px;
          display: block;
        }
        
        @media (min-width: 768px) {
          .profile-member-since {
            font-size: 14px;
            margin-top: 0;
            display: inline;
            margin-left: 12px;
          }
        }
        
        .profile-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 12px;
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.3);
          color: #22c55e;
          font-size: 13px;
          margin-top: 16px;
        }
        
        @media (min-width: 768px) {
          .profile-status-badge {
            padding: 12px 24px;
            font-size: 15px;
            margin-top: 0;
          }
        }
        
        .profile-sidebar-card {
          border-radius: 16px;
          margin-bottom: 16px;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-card {
            border-radius: 20px;
            margin-bottom: 0;
          }
        }
        
        .profile-sidebar-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 18px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-item {
            gap: 16px;
            padding: 24px 28px;
          }
        }
        
        .profile-sidebar-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-icon {
            width: 52px;
            height: 52px;
            border-radius: 14px;
          }
        }
        
        .profile-sidebar-icon svg {
          font-size: 20px;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-icon svg {
            font-size: 26px;
          }
        }
        
        .profile-sidebar-label h6 {
          margin: 0;
          font-size: 14px;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-label h6 {
            font-size: 16px;
          }
        }
        
        .profile-sidebar-label span {
          font-size: 11px;
          color: #94a3b8;
        }
        
        @media (min-width: 768px) {
          .profile-sidebar-label span {
            font-size: 13px;
          }
        }
        
        .profile-help-card {
          border-radius: 16px;
          background: linear-gradient(135deg, #c5a05e 0%, #dbb978 100%);
          margin-top: 16px;
        }
        
        @media (min-width: 768px) {
          .profile-help-card {
            border-radius: 20px;
            margin-top: 24px;
          }
        }
        
        .profile-content-card {
          border-radius: 16px;
        }
        
        @media (min-width: 768px) {
          .profile-content-card {
            border-radius: 20px;
          }
        }
        
        .profile-content-body {
          padding: 20px;
        }
        
        @media (min-width: 768px) {
          .profile-content-body {
            padding: 40px;
          }
        }
        
        .profile-section-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 6px;
        }
        
        @media (min-width: 768px) {
          .profile-section-title {
            font-size: 24px;
            margin-bottom: 8px;
          }
        }
        
        .profile-section-desc {
          color: #64748b;
          margin: 0;
          font-size: 13px;
        }
        
        @media (min-width: 768px) {
          .profile-section-desc {
            font-size: 15px;
          }
        }
        
        .profile-image-upload {
          background: linear-gradient(135deg, #fef9f0 0%, #fdf6e7 100%);
          border: 2px dashed #c5a05e;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 24px;
        }
        
        @media (min-width: 768px) {
          .profile-image-upload {
            padding: 24px;
            margin-bottom: 32px;
          }
        }
        
        .profile-image-preview {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-image-preview {
            width: 100px;
            height: 100px;
          }
        }
        
        .profile-form-label {
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
        }
        
        @media (min-width: 768px) {
          .profile-form-label {
            font-size: 14px;
            margin-bottom: 10px;
          }
        }
        
        .profile-form-input {
          height: 48px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          padding: 0 16px;
          font-size: 14px;
          width: 100%;
          transition: all 0.2s ease;
        }
        
        @media (min-width: 768px) {
          .profile-form-input {
            height: 56px;
            border-radius: 14px;
            padding: 0 20px;
            font-size: 15px;
          }
        }
        
        .profile-form-input:focus {
          border-color: #c5a05e;
          box-shadow: 0 0 0 3px rgba(197, 160, 94, 0.15);
          outline: none;
        }
        
        .profile-submit-btn {
          width: 100%;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #c5a05e 0%, #dbb978 100%);
          border: none;
          color: white;
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        @media (min-width: 768px) {
          .profile-submit-btn {
            width: auto;
            min-width: 200px;
            height: 56px;
            border-radius: 14px;
            font-size: 16px;
          }
        }
        
        .profile-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(197, 160, 94, 0.4);
        }
        
        .profile-mobile-tabs {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 12px;
          margin-bottom: 16px;
          -webkit-overflow-scrolling: touch;
        }
        
        @media (min-width: 992px) {
          .profile-mobile-tabs {
            display: none;
          }
        }
        
        .profile-mobile-tab {
          flex-shrink: 0;
          padding: 10px 16px;
          border-radius: 10px;
          border: 2px solid #e2e8f0;
          background: white;
          font-size: 13px;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        
        .profile-mobile-tab.active {
          background: linear-gradient(135deg, #c5a05e 0%, #dbb978 100%);
          border-color: #c5a05e;
          color: white;
        }
        
        .profile-desktop-sidebar {
          display: none;
        }
        
        @media (min-width: 992px) {
          .profile-desktop-sidebar {
            display: block;
          }
        }

        /* Image Upload Section */
        .profile-image-upload {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 16px;
          padding: 20px;
          border-radius: 16px;
          background: linear-gradient(135deg, #fef9f0 0%, #fdf6e7 100%);
          border: 2px dashed #c5a05e;
          margin-bottom: 24px;
        }
        
        @media (min-width: 768px) {
          .profile-image-upload {
            flex-direction: row;
            align-items: center;
            text-align: left;
            gap: 24px;
            padding: 28px;
            margin-bottom: 32px;
          }
        }
        
        .profile-upload-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 4px solid white;
          box-shadow: 0 8px 24px rgba(197, 160, 94, 0.3);
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-upload-avatar {
            width: 100px;
            height: 100px;
          }
        }
        
        .profile-upload-initial {
          font-size: 32px;
          font-weight: 700;
          color: white;
        }
        
        @media (min-width: 768px) {
          .profile-upload-initial {
            font-size: 40px;
          }
        }
        
        .profile-upload-camera {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #c5a05e;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        @media (min-width: 768px) {
          .profile-upload-camera {
            width: 32px;
            height: 32px;
          }
        }
        
        .profile-upload-info {
          flex: 1;
        }
        
        .profile-upload-title {
          margin: 0;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 6px;
          font-size: 15px;
        }
        
        .profile-upload-desc {
          margin: 0;
          font-size: 13px;
          color: #64748b;
          margin-bottom: 12px;
        }
        
        @media (min-width: 768px) {
          .profile-upload-desc {
            font-size: 14px;
          }
        }
        
        .profile-upload-btn {
          padding: 8px 16px;
          border-radius: 8px;
          background: #c5a05e;
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: none;
        }
        
        .profile-upload-remove {
          padding: 8px 16px;
          border-radius: 8px;
          background: #fee2e2;
          color: #dc3545;
          font-size: 13px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }

        /* Form Actions */
        .profile-form-actions {
          border-top: 2px solid #f1f5f9;
          padding-top: 20px;
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        @media (min-width: 768px) {
          .profile-form-actions {
            flex-direction: row;
            justify-content: flex-end;
            gap: 16px;
            padding-top: 28px;
            margin-top: 20px;
          }
        }
        
        .profile-btn-primary {
          height: 48px;
          padding: 0 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          background: linear-gradient(135deg, #c5a05e 0%, #b8934a 100%);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: white;
          width: 100%;
        }
        
        @media (min-width: 768px) {
          .profile-btn-primary {
            width: auto;
            height: 52px;
            padding: 0 32px;
            border-radius: 14px;
            font-size: 15px;
          }
        }
        
        .profile-btn-secondary {
          height: 48px;
          padding: 0 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          color: #ffffff;
          background: #1e293b;
          width: 100%;
          order: 1;
        }
        
        @media (min-width: 768px) {
          .profile-btn-secondary {
            width: auto;
            height: 52px;
            padding: 0 32px;
            border-radius: 14px;
            font-size: 15px;
            order: 0;
          }
        }
        
        .profile-btn-danger {
          height: 48px;
          padding: 0 24px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          background: #dc3545;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: white;
          width: 100%;
        }
        
        @media (min-width: 768px) {
          .profile-btn-danger {
            width: auto;
            height: 52px;
            padding: 0 32px;
            border-radius: 14px;
            font-size: 15px;
          }
        }

        /* Security Section */
        .profile-security-box {
          background: #fef2f2;
          border-radius: 14px;
          padding: 20px;
          margin-bottom: 24px;
        }
        
        @media (min-width: 768px) {
          .profile-security-box {
            border-radius: 16px;
            padding: 32px;
            margin-bottom: 32px;
          }
        }
        
        .profile-security-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #dc3545;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-security-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
          }
        }
        
        .profile-input-security {
          border-color: #fecaca !important;
          background: white !important;
        }
        
        .profile-security-tips {
          background: #f1f5f9;
          border-radius: 14px;
          padding: 20px;
        }
        
        @media (min-width: 768px) {
          .profile-security-tips {
            border-radius: 16px;
            padding: 28px;
          }
        }

        /* Notification Items */
        .profile-notification-item {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          border-radius: 14px;
          background: #f8fafc;
          border: 2px solid #f1f5f9;
        }
        
        @media (min-width: 768px) {
          .profile-notification-item {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 24px;
            border-radius: 16px;
          }
        }
        
        .profile-notification-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        @media (min-width: 768px) {
          .profile-notification-icon {
            width: 56px;
            height: 56px;
            border-radius: 14px;
          }
        }
        
        .profile-notification-text h6 {
          margin: 0;
          font-weight: 700;
          font-size: 15px;
          color: #1e293b;
        }
        
        @media (min-width: 768px) {
          .profile-notification-text h6 {
            font-size: 16px;
          }
        }
        
        .profile-notification-text span {
          font-size: 13px;
          color: #64748b;
        }
        
        @media (min-width: 768px) {
          .profile-notification-text span {
            font-size: 14px;
          }
        }
        
        .profile-switch {
          transform: scale(1.3);
        }
        
        @media (min-width: 768px) {
          .profile-switch {
            transform: scale(1.5);
          }
        }

        /* Form Select */
        .profile-form-select {
          height: 48px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          padding: 0 16px;
          font-size: 14px;
          width: 100%;
          transition: all 0.2s ease;
        }
        
        @media (min-width: 768px) {
          .profile-form-select {
            height: 56px;
            border-radius: 14px;
            padding: 0 20px;
            font-size: 15px;
          }
        }
        
        .profile-form-select:focus {
          border-color: #c5a05e;
          box-shadow: 0 0 0 3px rgba(197, 160, 94, 0.15);
          outline: none;
        }
      `}</style>
      {/* Alerts */}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          <IconifyIcon icon="bx:check-circle" className="me-2" />
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <IconifyIcon icon="bx:error-circle" className="me-2" />
          {error}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card className="border-0 profile-header-card">
        <div className="profile-header-inner">
          <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 gap-md-4">
            {/* Avatar */}
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar-inner">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>

            {/* User Info */}
            <div className="profile-header-info text-center text-md-start flex-grow-1">
              <h1>{user?.name || 'Utilisateur'}</h1>
              <p>{user?.email}</p>
              <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
                <span className="profile-role-badge" style={{ background: roleInfo.color }}>
                  <IconifyIcon icon={roleInfo.icon} />
                  {roleInfo.label}
                </span>
                <span className="profile-member-since">
                  <IconifyIcon icon="bx:calendar" className="me-1" />
                  Membre depuis {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'N/A'}
                </span>
              </div>
            </div>

            {/* Status Badge */}
            <div className="profile-status-badge">
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e' }} />
              Compte actif
            </div>
          </div>
        </div>
      </Card>

      {/* Mobile Tabs */}
      <div className="profile-mobile-tabs">
        {[
          { id: 'profile', icon: 'bx:user-circle', label: 'Profil' },
          { id: 'security', icon: 'bx:lock-alt', label: 'Sécurité' },
          { id: 'notifications', icon: 'bx:bell', label: 'Notifications' },
          { id: 'preferences', icon: 'bx:slider-alt', label: 'Préférences' }
        ].map((item) => (
          <button
            key={item.id}
            className={`profile-mobile-tab ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => setActiveTab(item.id)}
          >
            <IconifyIcon icon={item.icon} />
            {item.label}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <Row className="g-3 g-lg-4">
        {/* Sidebar - Desktop only */}
        <Col lg={4} className="profile-desktop-sidebar">
          <Card className="border-0 profile-sidebar-card">
            <CardBody className="p-0">
              {[
                { id: 'profile', icon: 'bx:user-circle', label: 'Mon Profil', desc: 'Informations personnelles' },
                { id: 'security', icon: 'bx:lock-alt', label: 'Sécurité', desc: 'Mot de passe et accès' },
                { id: 'notifications', icon: 'bx:bell', label: 'Notifications', desc: 'Préférences d\'alertes' },
                { id: 'preferences', icon: 'bx:slider-alt', label: 'Préférences', desc: 'Langue et affichage' }
              ].map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="profile-sidebar-item"
                  style={{
                    background: activeTab === item.id ? 'linear-gradient(135deg, #fef9f0 0%, #fdf6e7 100%)' : 'white',
                    borderLeft: activeTab === item.id ? '4px solid #c5a05e' : '4px solid transparent',
                    borderBottom: index < 3 ? '1px solid #f1f5f9' : 'none'
                  }}
                >
                  <div 
                    className="profile-sidebar-icon"
                    style={{
                      background: activeTab === item.id ? 'linear-gradient(135deg, #c5a05e 0%, #dbb978 100%)' : '#f1f5f9'
                    }}
                  >
                    <IconifyIcon 
                      icon={item.icon} 
                      style={{ color: activeTab === item.id ? 'white' : '#64748b' }} 
                    />
                  </div>
                  <div className="profile-sidebar-label">
                    <h6 style={{ 
                      fontWeight: activeTab === item.id ? '700' : '600',
                      color: activeTab === item.id ? '#c5a05e' : '#1e293b'
                    }}>
                      {item.label}
                    </h6>
                    <span>{item.desc}</span>
                  </div>
                  <IconifyIcon 
                    icon="bx:chevron-right" 
                    style={{ 
                      marginLeft: 'auto', 
                      fontSize: '22px', 
                      color: activeTab === item.id ? '#c5a05e' : '#cbd5e1' 
                    }} 
                  />
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Help Card */}
          <Card className="border-0 profile-help-card">
            <CardBody className="p-4 text-center">
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}>
                <IconifyIcon icon="bx:help-circle" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <h5 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>Besoin d'aide?</h5>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '16px' }}>
                Support disponible 24/7
              </p>
              <Button 
                variant="dark" 
                className="w-100"
                style={{ 
                  borderRadius: '10px', 
                  padding: '10px',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#ffffff',
                  background: '#1e293b'
                }}
                onClick={() => window.open('https://wamad.ma/#contact', '_blank')}
              >
                <IconifyIcon icon="bx:chat" className="me-2" style={{ color: '#ffffff' }} />
                Contacter
              </Button>
            </CardBody>
          </Card>
        </Col>

        {/* Content */}
        <Col lg={8}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card className="border-0 profile-content-card">
              <CardBody className="profile-content-body">
                <div className="mb-4">
                  <h3 className="profile-section-title">Informations personnelles</h3>
                  <p className="profile-section-desc">
                    Mettez à jour vos informations de profil
                  </p>
                </div>

                <Form onSubmit={handleProfileSubmit}>
                  {/* Profile Image Upload */}
                  <div className="profile-image-upload">
                    <div style={{ position: 'relative' }}>
                      <div 
                        className="profile-upload-avatar"
                        style={{
                          background: imagePreview ? `url(${imagePreview}) center/cover` : 'linear-gradient(135deg, #c5a05e 0%, #dbb978 100%)'
                        }}
                      >
                        {!imagePreview && (
                          <span className="profile-upload-initial">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      <label htmlFor="profileImageInput" className="profile-upload-camera">
                        <IconifyIcon icon="bx:camera" style={{ color: 'white', fontSize: '16px' }} />
                      </label>
                      <input
                        type="file"
                        id="profileImageInput"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="profile-upload-info">
                      <h6 className="profile-upload-title">Photo de profil</h6>
                      <p className="profile-upload-desc">JPG, PNG ou GIF. Max 2MB</p>
                      <div className="d-flex gap-2 flex-wrap">
                        <label htmlFor="profileImageInput" className="profile-upload-btn">
                          <IconifyIcon icon="bx:upload" />
                          Changer
                        </label>
                        {imagePreview && (
                          <button
                            type="button"
                            onClick={() => { setImagePreview(null); setProfileImage(null); }}
                            className="profile-upload-remove"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <Row className="g-3 g-md-4 mb-4">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Nom complet</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={profileData.name}
                          onChange={handleInputChange}
                          placeholder="Votre nom"
                          required
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Adresse email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={profileData.email}
                          onChange={handleInputChange}
                          placeholder="email@exemple.com"
                          required
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 g-md-4 mb-4">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Téléphone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={profileData.phone}
                          onChange={handleInputChange}
                          placeholder="06 12 34 56 78"
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Adresse</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={profileData.address}
                          onChange={handleInputChange}
                          placeholder="Votre adresse complète"
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 g-md-4 mb-4">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Ville</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={profileData.city}
                          onChange={handleInputChange}
                          placeholder="Ex: Casablanca"
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="profile-form-label">Code postal</Form.Label>
                        <Form.Control
                          type="text"
                          name="postal_code"
                          value={profileData.postal_code}
                          onChange={handleInputChange}
                          placeholder="Ex: 20000"
                          className="profile-form-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="profile-form-actions">
                    <Button variant="dark" className="profile-btn-secondary">
                      Annuler
                    </Button>
                    <Button type="submit" disabled={loading} className="profile-btn-primary">
                      {loading ? (
                        <><Spinner size="sm" /> Enregistrement...</>
                      ) : (
                        <><IconifyIcon icon="bx:save" style={{ fontSize: '20px' }} /> Enregistrer</>
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card className="border-0 profile-content-card">
              <CardBody className="profile-content-body">
                <div className="mb-4">
                  <h3 className="profile-section-title">Sécurité du compte</h3>
                  <p className="profile-section-desc">
                    Gérez votre mot de passe et la sécurité de votre compte
                  </p>
                </div>

                <div className="profile-security-box">
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="profile-security-icon">
                      <IconifyIcon icon="bx:lock-alt" style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <h5 style={{ margin: 0, fontWeight: '700', color: '#1e293b' }}>
                      Changer le mot de passe
                    </h5>
                  </div>

                  <Form onSubmit={handlePasswordSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="profile-form-label">Mot de passe actuel</Form.Label>
                      <Form.Control
                        type="password"
                        name="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        placeholder="••••••••"
                        required
                        className="profile-form-input profile-input-security"
                      />
                    </Form.Group>

                    <Row className="g-3 g-md-4 mb-4">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="profile-form-label">Nouveau mot de passe</Form.Label>
                          <Form.Control
                            type="password"
                            name="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            placeholder="••••••••"
                            required
                            minLength={8}
                            className="profile-form-input profile-input-security"
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="profile-form-label">Confirmer le mot de passe</Form.Label>
                          <Form.Control
                            type="password"
                            name="new_password_confirmation"
                            value={passwordData.new_password_confirmation}
                            onChange={handlePasswordChange}
                            placeholder="••••••••"
                            required
                            minLength={8}
                            className="profile-form-input profile-input-security"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button type="submit" disabled={loading} className="profile-btn-danger">
                      {loading ? (
                        <><Spinner size="sm" /> Modification...</>
                      ) : (
                        <><IconifyIcon icon="bx:lock" style={{ fontSize: '20px' }} /> Mettre à jour</>
                      )}
                    </Button>
                  </Form>
                </div>

                {/* Security Tips */}
                <div className="profile-security-tips">
                  <h6 style={{ fontWeight: '700', marginBottom: '16px', color: '#1e293b' }}>
                    <IconifyIcon icon="bx:bulb" className="me-2" style={{ color: '#f59e0b' }} />
                    Conseils de sécurité
                  </h6>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', lineHeight: '2' }}>
                    <li>Utilisez au moins 8 caractères</li>
                    <li>Mélangez majuscules, minuscules et chiffres</li>
                    <li>Ne partagez jamais votre mot de passe</li>
                    <li>Changez-le régulièrement</li>
                  </ul>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card className="border-0 profile-content-card">
              <CardBody className="profile-content-body">
                <div className="mb-4">
                  <h3 className="profile-section-title">Préférences de notification</h3>
                  <p className="profile-section-desc">
                    Choisissez comment vous souhaitez être notifié
                  </p>
                </div>

                <div className="d-flex flex-column gap-3 gap-md-4">
                  {[
                    { icon: 'bx:envelope', title: 'Notifications par email', desc: 'Recevez des mises à jour par email', enabled: true, color: '#3b82f6' },
                    { icon: 'bx:package', title: 'Mises à jour de commande', desc: 'Notifications sur vos commandes', enabled: true, color: '#22c55e' },
                    { icon: 'bx:gift', title: 'Offres spéciales', desc: 'Promotions et réductions exclusives', enabled: false, color: '#f59e0b' },
                    { icon: 'bx:news', title: 'Newsletter', desc: 'Actualités et nouveaux produits', enabled: false, color: '#8b5cf6' }
                  ].map((item, index) => (
                    <div key={index} className="profile-notification-item">
                      <div className="d-flex align-items-center gap-3 gap-md-4">
                        <div className="profile-notification-icon" style={{ background: `${item.color}15` }}>
                          <IconifyIcon icon={item.icon} style={{ fontSize: '24px', color: item.color }} />
                        </div>
                        <div className="profile-notification-text">
                          <h6>{item.title}</h6>
                          <span>{item.desc}</span>
                        </div>
                      </div>
                      <Form.Check type="switch" defaultChecked={item.enabled} className="profile-switch" />
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <Card className="border-0 profile-content-card">
              <CardBody className="profile-content-body">
                <div className="mb-4">
                  <h3 className="profile-section-title">Préférences</h3>
                  <p className="profile-section-desc">
                    Personnalisez votre expérience
                  </p>
                </div>

                <Row className="g-3 g-md-4 mb-4">
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className="profile-form-label">
                        <IconifyIcon icon="bx:globe" className="me-2" />
                        Langue
                      </Form.Label>
                      <Form.Select className="profile-form-select">
                        <option>🇫🇷 Français</option>
                        <option>🇲🇦 العربية</option>
                        <option>🇬🇧 English</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label className="profile-form-label">
                        <IconifyIcon icon="bx:dollar-circle" className="me-2" />
                        Devise
                      </Form.Label>
                      <Form.Select className="profile-form-select">
                        <option>MAD - Dirham Marocain</option>
                        <option>EUR - Euro</option>
                        <option>USD - Dollar US</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="profile-form-actions" style={{ justifyContent: 'flex-end' }}>
                  <Button className="profile-btn-primary">
                    <IconifyIcon icon="bx:save" style={{ fontSize: '20px' }} />
                    Enregistrer
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
